import { useCallback, useEffect, useState } from "react";
import { useSubmitGalleryInquiryMutation } from "../Slices/roomNateApis";
import { classifySessionAudience, normalizeEmail } from "../utils/sessionAudience";
import { useAnalytics } from "../utils/eventLogAnalytics";
import { getAnalyticsTargetWindow } from "../utils/analyticsTargetWindow";
import {
  galleryInquiryConfirmStateFromSession,
  layoutInquirySessionKey,
  readGalleryInquirySession,
  readGalleryInterestConsentEmail,
  recordGalleryInterestConsent,
  writeGalleryInquirySession,
} from "../utils/galleryInquiryUx";

function readGuestSessionId() {
  try {
    return localStorage.getItem("mf_gsid") || null;
  } catch {
    return null;
  }
}

/**
 * Submit a gallery inquiry. Fires inquiry_submitted ONLY after backend 200 on first submit.
 *
 * Carries no delivery state. The property-side send is attempted after the
 * response returns, so anything the browser could report here would be a
 * constant rather than an outcome; the authoritative result is the inquiry's
 * routing_status and the server-side send log, keyed to the same inquiry.
 */
export function useGalleryInquiry({
  layoutUuid,
  layoutMongoId,
  layout = null,
  customerRef,
  isGuest,
  userEmail,
  isInternal = false,
  inquiriesAvailable = true,
}) {
  const [submitGalleryInquiry] = useSubmitGalleryInquiryMutation();
  const { eventLog } = useAnalytics(customerRef || null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [error, setError] = useState("");

  const layoutKey = layout
    ? layoutInquirySessionKey(layout)
    : layoutInquirySessionKey(layoutMongoId || layoutUuid);

  useEffect(() => {
    setConfirmState(galleryInquiryConfirmStateFromSession(layoutKey));
  }, [layoutKey]);

  /** The address we can send without asking for one. Guests never have one. */
  const knownEmail = isGuest ? "" : normalizeEmail(userEmail) || "";
  const [consentedEmail, setConsentedEmail] = useState(
    readGalleryInterestConsentEmail
  );
  const hasInterestConsent =
    Boolean(knownEmail) && consentedEmail === knownEmail;

  const submit = useCallback(
    async (emailInput, { flow } = {}) => {
      const email = normalizeEmail(emailInput);
      if (!email) {
        setError("Enter a valid email address.");
        return false;
      }
      if (!layoutUuid && !layoutMongoId) {
        setError("This gallery isn't set up to receive interest yet.");
        return false;
      }

      /**
       * Callers that know which path the tap took say so. Everything else came
       * through the modal, where submitting the address we already held is the
       * one-time confirmation and anything else is a typed address.
       */
      const resolvedFlow =
        flow || (email === knownEmail ? "first_confirm" : "email_form");

      setSubmitting(true);
      setError("");

      try {
        const guestSessionId = readGuestSessionId();
        const audience = classifySessionAudience({
          isGuest,
          email,
          isInternal,
        });

        const response = await submitGalleryInquiry({
          layoutUuid,
          layoutMongoId,
          inquirerEmail: email,
          guestSessionId,
          audience,
        }).unwrap();

        const created = response?.created === true;
        const alreadySubmitted = response?.alreadySubmitted === true || !created;

        if (created) {
          const galleryId = layoutMongoId || layoutUuid;
          eventLog(getAnalyticsTargetWindow(), "floor_plan_event", "inquiry_submitted", {
            galleryId,
            galleryUrl:
              layoutMongoId && typeof window !== "undefined"
                ? `${window.location.origin}/floor-gallery/${layoutMongoId}`
                : undefined,
            customer_ref: response?.customer_ref || customerRef || undefined,
            inquirerEmail: email,
            audience: response?.audience || audience,
            source: "gallery_inquire",
            flow: resolvedFlow,
          });
        }

        // Consent is recorded against the address, not the listing: having agreed
        // once, this viewer's later taps carry the same meaning on any listing.
        if (email === knownEmail) {
          recordGalleryInterestConsent(email);
          setConsentedEmail(email);
        }

        writeGalleryInquirySession(layoutKey, { email, submitted: true });
        setConfirmState({ email, kind: "submitted" });
        return { ok: true, email, created, alreadySubmitted };
      } catch (err) {
        const message =
          err?.data?.error ||
          err?.error ||
          "Something went wrong. Please try again.";
        setError(typeof message === "string" ? message : "Something went wrong.");
        return { ok: false };
      } finally {
        setSubmitting(false);
      }
    },
    [
      layoutUuid,
      layoutMongoId,
      layoutKey,
      customerRef,
      isGuest,
      isInternal,
      knownEmail,
      submitGalleryInquiry,
      eventLog,
    ]
  );

  /**
   * Viewers whose email we already hold have nothing to fill in, so the CTA is
   * the submit and no form is shown.
   *
   * Consent gates this rather than merely holding an address, because sending
   * has to be something the viewer chose knowingly at least once: the address
   * came from registration, where nothing was said about property teams. Their
   * first interest goes through the modal, which states what tapping does and
   * that later taps will do it without asking again. From then on the tap is
   * the submit.
   *
   * Availability is in the gate too — the modal is the only surface that can
   * explain a listing which isn't accepting interest yet, so skipping it would
   * turn that into a dead tap.
   */
  const canOneTap =
    hasInterestConsent && Boolean(inquiriesAvailable) && !confirmState;

  const submitKnown = useCallback(() => {
    if (!knownEmail || submitting) return Promise.resolve({ ok: false });
    return submit(knownEmail, { flow: "one_tap" });
  }, [knownEmail, submitting, submit]);

  /**
   * Every tap of the real CTA, on the surface it happened. `flow` records which
   * path the tap took, so the three completion rates are never read as one
   * number — blended, that rate tracks the signed-in/guest mix and how many
   * viewers are on their first interest, rather than anything about the funnel.
   * Re-taps once interest is sent are not new interest and are not counted.
   */
  const trackIntent = useCallback(
    (surface) => {
      const galleryId = layoutMongoId || layoutUuid;
      if (!galleryId || confirmState) return;
      eventLog(getAnalyticsTargetWindow(), "floor_plan_event", "inquiry_intent", {
        galleryId,
        galleryUrl:
          layoutMongoId && typeof window !== "undefined"
            ? `${window.location.origin}/floor-gallery/${layoutMongoId}`
            : undefined,
        customer_ref: customerRef || undefined,
        source: "gallery_inquire",
        surface,
        flow: canOneTap ? "one_tap" : knownEmail ? "first_confirm" : "email_form",
      });
    },
    [
      layoutMongoId,
      layoutUuid,
      customerRef,
      eventLog,
      confirmState,
      canOneTap,
      knownEmail,
    ]
  );

  /**
   * Opening the form is the denominator for viewer-to-interest conversion —
   * without it, `inquiry_submitted` alone cannot show how many viewers opened
   * the modal and walked away. `surface` separates gallery from editor so the
   * two entry points can be compared. Dismissals stay implicit (opened minus
   * submitted) rather than doubling event volume in the activity feed.
   */
  const trackModalOpened = useCallback(
    (surface) => {
      const galleryId = layoutMongoId || layoutUuid;
      if (!galleryId) return;
      eventLog(getAnalyticsTargetWindow(), "floor_plan_event", "inquiry_modal_opened", {
        galleryId,
        galleryUrl:
          layoutMongoId && typeof window !== "undefined"
            ? `${window.location.origin}/floor-gallery/${layoutMongoId}`
            : undefined,
        customer_ref: customerRef || undefined,
        source: "gallery_inquire",
        surface,
      });
    },
    [layoutMongoId, layoutUuid, customerRef, eventLog]
  );

  const reset = useCallback(() => {
    setError("");
    setSubmitting(false);
  }, []);

  const session = readGalleryInquirySession(layoutKey);
  const guestEmailPrefill =
    isGuest && !confirmState ? session?.email || "" : "";

  return {
    submit,
    submitting,
    confirmState,
    error,
    reset,
    guestEmailPrefill,
    knownEmail,
    canOneTap,
    submitKnown,
    trackIntent,
    trackModalOpened,
  };
}
